import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "error", "licenseOption", "budgetValue", "budgetInput"]

  connect() {
    this.setupKeyboardNavigation()
    this.applySearchParamsFromUrl()
  }

  // Bezoekers die direct op /search landen worden door de server met hun
  // filters naar de homepage gestuurd; voer die zoekopdracht hier meteen uit.
  // De controller hangt ook op een omliggende div; alleen de form-instantie
  // mag submitten, anders crasht requestSubmit en dubbelt de zoekopdracht.
  applySearchParamsFromUrl() {
    if (this.element.tagName !== "FORM") return

    const params = new URLSearchParams(window.location.search)
    const licenseType = params.get('license_type')
    if (!licenseType) return

    const radio = this.element.querySelector(`input[name="license_type"][value="${CSS.escape(licenseType)}"]`)
    if (!radio) return

    radio.checked = true
    radio.dispatchEvent(new Event('change', { bubbles: true }))

    const budget = params.get('budget')
    if (budget && this.hasBudgetInputTarget) {
      this.budgetInputTarget.value = budget
    }

    this.element.requestSubmit()
  }

  setupKeyboardNavigation() {
    // Add keyboard navigation for license type selection
    const licenseRadios = Array.from(this.element.querySelectorAll('input[name="license_type"]'))

    licenseRadios.forEach((radio, index) => {
      const label = radio.closest('label')
      if (!label) return

      label.addEventListener('keydown', (e) => {
        let targetIndex = -1

        switch(e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault()
            radio.checked = true
            radio.dispatchEvent(new Event('change', { bubbles: true }))
            break

          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault()
            targetIndex = (index + 1) % licenseRadios.length
            break

          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault()
            targetIndex = (index - 1 + licenseRadios.length) % licenseRadios.length
            break

          case 'Home':
            e.preventDefault()
            targetIndex = 0
            break

          case 'End':
            e.preventDefault()
            targetIndex = licenseRadios.length - 1
            break
        }

        if (targetIndex >= 0) {
          const targetRadio = licenseRadios[targetIndex]
          const targetLabel = targetRadio.closest('label')
          targetLabel.focus()
          targetRadio.checked = true
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
    })
  }

  setBudget(event) {
    const budget = event.currentTarget.dataset.budget
    if (this.hasBudgetInputTarget) {
      this.budgetInputTarget.value = budget

      // Add visual feedback to the clicked button
      const allButtons = this.element.querySelectorAll('.budget-preset')
      allButtons.forEach(btn => {
        btn.classList.remove('border-emerald-500', 'bg-emerald-50', 'text-emerald-600')
        btn.classList.add('border-gray-200', 'text-gray-700')
      })

      event.currentTarget.classList.remove('border-gray-200', 'text-gray-700')
      event.currentTarget.classList.add('border-emerald-500', 'bg-emerald-50', 'text-emerald-600')

      // Trigger input event for any listeners
      this.budgetInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  updateBudgetDisplay() {
    // This method can be used if we need to do additional formatting
    // Currently the native number input handles display
  }

  hideLicenseError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  handleLicenseChange(event) {
    // Hide error message
    this.hideLicenseError()

    // Update visual selection state
    const allLabels = this.element.querySelectorAll('.license-option')
    const selectedLabel = event.target.closest('.license-option')

    allLabels.forEach(label => {
      const card = label.querySelector('.card-container')
      const indicator = label.querySelector('.indicator')
      const checkmark = label.querySelector('.checkmark')
      const labelText = label.querySelector('.label-text')
      const labelDescription = label.querySelector('.label-description')
      const iconContainer = label.querySelector('.icon-container')
      const iconSvg = label.querySelector('.icon-svg')

      // Remove selected state
      label.classList.remove('selected')
      card.classList.remove('border-emerald-500', 'bg-gradient-to-br', 'from-emerald-50', 'to-white', 'shadow-lg')
      card.classList.add('border-gray-200', 'bg-white')
      indicator.classList.remove('border-emerald-500', 'bg-emerald-500', 'shadow-md')
      indicator.classList.add('border-gray-300', 'bg-white')
      checkmark.classList.add('opacity-0')
      labelText.classList.remove('text-emerald-600')
      labelText.classList.add('text-gray-900')
      if (labelDescription) {
        labelDescription.classList.remove('text-emerald-600')
        labelDescription.classList.add('text-gray-500')
      }
      if (iconContainer) {
        iconContainer.classList.remove('bg-emerald-100', 'shadow-sm')
        iconContainer.classList.add('bg-gray-100')
      }
      if (iconSvg) {
        iconSvg.classList.remove('text-emerald-600')
        iconSvg.classList.add('text-gray-600')
      }
    })

    // Add selected state to the clicked label
    if (selectedLabel) {
      const card = selectedLabel.querySelector('.card-container')
      const indicator = selectedLabel.querySelector('.indicator')
      const checkmark = selectedLabel.querySelector('.checkmark')
      const labelText = selectedLabel.querySelector('.label-text')
      const labelDescription = selectedLabel.querySelector('.label-description')
      const iconContainer = selectedLabel.querySelector('.icon-container')
      const iconSvg = selectedLabel.querySelector('.icon-svg')

      selectedLabel.classList.add('selected')
      card.classList.remove('border-gray-200', 'bg-white')
      card.classList.add('border-emerald-500', 'bg-gradient-to-br', 'from-emerald-50', 'to-white', 'shadow-lg')
      indicator.classList.remove('border-gray-300', 'bg-white')
      indicator.classList.add('border-emerald-500', 'bg-emerald-500', 'shadow-md')
      checkmark.classList.remove('opacity-0')
      labelText.classList.remove('text-gray-900')
      labelText.classList.add('text-emerald-600')
      if (labelDescription) {
        labelDescription.classList.remove('text-gray-500')
        labelDescription.classList.add('text-emerald-600')
      }
      if (iconContainer) {
        iconContainer.classList.remove('bg-gray-100')
        iconContainer.classList.add('bg-emerald-100', 'shadow-sm')
      }
      if (iconSvg) {
        iconSvg.classList.remove('text-gray-600')
        iconSvg.classList.add('text-emerald-600')
      }
    }
  }

  submit(event) {
    event.preventDefault()
    const form = this.element
    const formData = new FormData(form)
    
    // Check if a license type is selected
    const selectedLicenseType = formData.get('license_type')
    if (!selectedLicenseType) {
      this.errorTarget.classList.remove('hidden')
      return
    }
    
    // Convert FormData to URLSearchParams
    const params = new URLSearchParams(formData)
    
    fetch(`/search?${params}`, {
      headers: {
        'Accept': 'text/html'
      }
    })
    .then(response => response.text())
    .then(html => {
      const resultsContainer = document.getElementById('search-results-container')
      if (resultsContainer) {
        resultsContainer.innerHTML = html
        resultsContainer.classList.remove('hidden')
        // Update filter values
        this.updateFilters(formData)
        resultsContainer.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
    })
  }

  updateFilters(formData) {
    // Update budget filter
    const budgetFilter = document.querySelector('input[name="budget"]')
    if (budgetFilter) {
      budgetFilter.value = formData.get('budget')
    }

    // Update license type radio buttons
    const selectedType = formData.get('license_type')
    const licenseTypeRadios = document.querySelectorAll('input[name="license_type"]')
    licenseTypeRadios.forEach(radio => {
      radio.checked = radio.value === selectedType
    })
  }
}